import React, { useState, useEffect, useCallback } from 'react';
import { DataGrid, GridRowModes, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { apiUrl } from '../utils/api';
import { Tooltip, Box, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import { dataGridSx } from '../utils/dataGridStyles';

const vertHeader = text => (
  <div
    style={{
      writingMode: 'vertical-rl',
      height: '100%',
      display: 'flex',
      alignItems: 'flex-end', // bottom-align within header cell
      justifyContent: 'center'
    }}
  >
    {text}
  </div>
);

const bottomHeader = text => (
  <div
    style={{
      height: '100%',
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center'
    }}
  >
    {text}
  </div>
);

const SiteTeam = ({ projectId, access = 'none' }) => {
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]); // list of {id, document_location}
  const [rowModesModel, setRowModesModel] = useState({});
  // Loading flag drives the DataGrid's built-in loader so the user sees a
  // spinner-over-table state instead of an empty grid while the three
  // parallel fetches run. Same pattern used on the Design Changes page.
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(() => {
    if (access === 'none' || !projectId) {
      setRows([]);
      setLocations([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      fetch(apiUrl(`/api/design-changes?project_id=${projectId}`)).then(r => r.json()),
      fetch(apiUrl(`/api/document-locations?project_id=${projectId}`)).then(r => r.json()),
      fetch(apiUrl(`/api/location-statuses?project_id=${projectId}`)).then(r => r.json())
    ]).then(([changes, locs, statuses]) => {
      // Build a lookup of all three per-location statuses keyed by (DC, location).
      // site_location_status = Design status (existing); material_location_status
      // and subcon_location_status are the new per-location fields.
      const statusMap = {};
      statuses.forEach(s => {
        statusMap[`${s.change_number}::${s.document_location_id}`] = {
          design: s.site_location_status || '',
          material: s.material_location_status || '',
          subcon: s.subcon_location_status || '',
        };
      });
      const rowData = changes.map(c => {
        const rec = { id: c.change_number, ...c };
        locs.forEach(l => {
          const k = `${c.change_number}::${l.id}`;
          const m = statusMap[k] || { design: '', material: '', subcon: '' };
          rec[`loc_${l.id}`] = m.design;
          rec[`mat_${l.id}`] = m.material;
          rec[`sub_${l.id}`] = m.subcon;
        });
        return rec;
      });
      setRows(rowData);
      setLocations(locs);
    })
    .catch(() => {
      // On any fetch failure leave the table empty but stop showing the
      // loader so the user isn't stuck on a spinner forever.
    })
    .finally(() => setLoading(false));
  }, [projectId, access]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (access === 'none') {
    return <div>Access denied</div>;
  }

  const processRowUpdate = updatedRow => {
    const { id, material_status, subcon_status, site_remarks } = updatedRow;
    const changeReq = fetch(apiUrl(`/api/design-changes/${id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ material_status, subcon_status, site_remarks })
    });

    // For each location, save all three per-location statuses in one PUT.
    // The backend now does a selective update so unsent keys are not touched,
    // but here we always send all three since the row's edit covers all three.
    const statusReqs = locations.map(loc => {
      const designField = `loc_${loc.id}`;
      const matField = `mat_${loc.id}`;
      const subField = `sub_${loc.id}`;
      return fetch(apiUrl('/api/location-statuses'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          change_number: id,
          document_location_id: loc.id,
          site_location_status: updatedRow[designField] || '',
          material_location_status: updatedRow[matField] || '',
          subcon_location_status: updatedRow[subField] || ''
        })
      });
    });

    return Promise.all([changeReq, ...statusReqs])
      .then(responses => {
        if (responses.some(r => !r.ok)) throw new Error('Update failed');
        return Promise.all(responses.map(r => r.json()));
      })
      .then(() => { fetchData(); return updatedRow; })
      .catch(err => { alert(err.message); throw err; });
  };

  const handleEditClick = id => () => setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  const handleSaveClick = id => () => setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  const handleCancelClick = id => () => setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View, ignoreModifications: true } });

  // Shared cell renderer (bottom-aligned, centered) for all three per-location
  // status columns so the design/material/subcon triples line up cleanly.
  const locCell = params => (
    <div
      style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center'
      }}
    >
      {params.value}
    </div>
  );

  const columns: GridColDef[] = [
    { field: 'change_number', headerName: 'Number', width: 180, headerAlign: 'center', align: 'center' },
    { field: 'change_name', headerName: 'Name', flex: 1, minWidth: 240, headerAlign: 'left' },
    // Per-Document-Location editable columns. Each location contributes three
    // columns — Design (existing), Material, Subcon. Headers are short and
    // include the location name as a prefix (e.g. "SGA0 Design"), so they
    // read horizontally and stay consistent with every other page's table.
    ...locations.flatMap(loc => ([
      {
        field: `loc_${loc.id}`,
        headerName: `${loc.document_location} Design`,
        editable: true,
        type: 'singleSelect' as any,
        valueOptions: ['NA', 'Pending', 'Completed'],
        width: 140,
        headerAlign: 'center' as any,
        align: 'center' as any
      },
      {
        field: `mat_${loc.id}`,
        headerName: `${loc.document_location} Material`,
        editable: true,
        type: 'singleSelect' as any,
        valueOptions: ['NA', 'Pending', 'Procured'],
        width: 140,
        headerAlign: 'center' as any,
        align: 'center' as any
      },
      {
        field: `sub_${loc.id}`,
        headerName: `${loc.document_location} Subcon`,
        editable: true,
        type: 'singleSelect' as any,
        valueOptions: ['NA', 'Pending', 'Assigned'],
        width: 140,
        headerAlign: 'center' as any,
        align: 'center' as any
      }
    ])),
    { field: 'change_status', headerName: 'Design Status', width: 130, headerAlign: 'center', align: 'center' },
    {
      field: 'material_status',
      headerName: 'Material',
      editable: true,
      width: 120,
      type: 'singleSelect' as any,
      valueOptions: ['NA', 'Pending', 'Procured'],
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'subcon_status',
      headerName: 'Subcon',
      editable: true,
      width: 120,
      type: 'singleSelect' as any,
      valueOptions: ['NA', 'Pending', 'Assigned'],
      headerAlign: 'center',
      align: 'center'
    },
    { field: 'site_remarks', headerName: 'Remarks', width: 200, editable: true, headerAlign: 'left' },
    {
      field: 'actions',
      type: 'actions' as any,
      headerName: 'Actions',
      width: 90,
      headerAlign: 'center' as any,
      getActions: ({ id }) => {
        const isEditing = rowModesModel[id]?.mode === GridRowModes.Edit;
        return isEditing
          ? [
              <GridActionsCellItem icon={<Tooltip title="Save"><SaveIcon /></Tooltip>} label="Save" onClick={handleSaveClick(id)} showInMenu={false} key="save" />,
              <GridActionsCellItem icon={<Tooltip title="Cancel"><CancelIcon /></Tooltip>} label="Cancel" onClick={handleCancelClick(id)} showInMenu={false} key="cancel" />
            ]
          : [<GridActionsCellItem icon={<Tooltip title="Edit"><EditIcon /></Tooltip>} label="Edit" onClick={handleEditClick(id)} showInMenu={false} key="edit" />];
      }
    }
  ];

  if (!projectId) return <div>Please select a project.</div>;

  const pendingCount = rows.filter(r => r.change_status === 'Pending').length;
  const completedCount = rows.filter(r => r.change_status === 'Completed').length;
  const naCount = rows.filter(r => r.change_status === 'NA').length;

  return (
    <div style={{ padding: 16 }}>
      {rows.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          {[
            { label: 'Pending', count: pendingCount, color: pendingCount > 0 ? '#d32f2f' : 'text.secondary' },
            { label: 'Completed', count: completedCount, color: 'success.main' },
            { label: 'NA', count: naCount, color: 'text.secondary' },
            { label: 'Total', count: rows.length, color: 'primary.main' },
          ].map(({ label, count, color }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.75, borderRadius: 2, bgcolor: 'grey.100', border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>{label}</Typography>
              <Typography variant="body2" fontWeight={700} color={color}>{count}</Typography>
            </Box>
          ))}
        </Box>
      )}
      {/* Wrapper mirrors the Design Changes page so the table looks the same
          across the app — white card, rounded corners, soft border. The grid
          autoHeights to its rows so an empty state stays compact. */}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden', background: 'white' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={r => r.id}
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={setRowModesModel}
          processRowUpdate={processRowUpdate}
          loading={loading}
          autoHeight
          rowHeight={48}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          // Shared styling so the Site Tracker table matches every other
          // table in the app — uniform stripes, bold gray headers, clear
          // selected-row highlight.
          sx={{ ...dataGridSx, '& .MuiDataGrid-row': { ...dataGridSx['& .MuiDataGrid-row'], cursor: 'pointer' } }}
        />
      </Box>
    </div>
  );
};

export default SiteTeam;
